import { AnalyticsReportAgentRunStatus } from "@redotech/cockroachdb-codegen/tables/analytics-report-agent-run-status";
import { AnalyticsReportCreationSource } from "@redotech/cockroachdb-codegen/tables/analytics-report-creation-source";
import { AnalyticsReportStatus } from "@redotech/cockroachdb-codegen/tables/analytics-report-status";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const SnowflakeSqlBindSchema = z.union([
  z.string(),
  z.number(),
  z.array(z.union([z.string(), z.number()])),
]);

const DebugChatMessageSchema = z.object({
  index: z.number(),
  id: z.string().nullable(),
  role: z.enum(["system", "user", "assistant", "tool"]),
  content: z.unknown(),
  usage: z.unknown().nullable(),
});

const DebugChatThreadSchema = z.object({
  threadId: z.string(),
  found: z.boolean(),
  error: z.string().nullable(),
  agentName: z.string().nullable(),
  teamId: z.string().nullable(),
  parentThreadId: z.string().nullable(),
  userDisplayName: z.string().nullable(),
  messageCount: z.number(),
  iterationCount: z.number().nullable(),
  state: z.unknown().nullable(),
  output: z.unknown().nullable(),
  createdAt: z.date().nullable(),
  updatedAt: z.date().nullable(),
  messages: z.array(DebugChatMessageSchema),
});

const DebugAgentRunSchema = z.object({
  id: z.string(),
  reportRevisionId: z.string().nullable(),
  threadId: z.string(),
  parentThreadId: z.string().nullable(),
  toolCallId: z.string().nullable(),
  requestedQuestion: z.string(),
  confirmedPlan: z.string().nullable(),
  status: z.enum(AnalyticsReportAgentRunStatus),
  failureCode: z.string().nullable(),
  progressMessage: z.string().nullable(),
  createdAt: z.date(),
  completedAt: z.date().nullable(),
});

const DebugRevisionSchema = z.object({
  id: z.string(),
  revisionNumber: z.string(),
  queryVersion: z.number(),
  title: z.string().nullable(),
  titleManuallyEdited: z.boolean(),
  sourceAgentRunId: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdAt: z.date(),
  semanticQuery: z.unknown(),
  columnPresentation: z.unknown().nullable(),
});

export const getCustomReportDebug = unaryRpc({
  name: "getCustomReportDebug" as const,
  input: z.object({
    id: z.string().trim().min(1),
    teamId: z.string().trim().optional(),
  }),
  output: z.object({
    valid: z.boolean(),
    rowId: z.string().nullable(),
    publicId: z.string().nullable(),
    found: z.boolean(),
    error: z.string().nullable(),
    report: z
      .object({
        teamId: z.string(),
        title: z.string().nullable(),
        status: z.enum(AnalyticsReportStatus),
        creationSource: z.enum(AnalyticsReportCreationSource),
        requestedQuestion: z.string(),
        reportSpec: z.string().nullable(),
        explanation: z.string().nullable(),
        errorMessage: z.string().nullable(),
        createdAt: z.date(),
        updatedAt: z.date(),
      })
      .nullable(),
    highLevelAst: z.object({
      available: z.boolean(),
      message: z.string().nullable(),
      value: z.unknown().nullable(),
    }),
    queryPlan: z.unknown().nullable(),
    queryPlanVersion: z.number().nullable(),
    agentRuns: z.array(DebugAgentRunSchema),
    revisions: z.array(DebugRevisionSchema),
    chatThreads: z.array(DebugChatThreadSchema),
    snowflake: z
      .object({
        boundSql: z.string(),
        inlineSql: z.string().nullable(),
        bindings: z.array(SnowflakeSqlBindSchema),
        reportTimeZone: z.string(),
        warning: z.string().nullable(),
      })
      .nullable(),
  }),
});

export type GetCustomReportDebugInput = z.infer<
  typeof getCustomReportDebug.input
>;
export type GetCustomReportDebugOutput = z.infer<
  typeof getCustomReportDebug.output
>;
